import 'regenerator-runtime/runtime'
import EventEmitter from 'events'
import shuffle from 'shuffle-array'

import Player from './Player'
import Deck from './Deck'
import WheatField from './cards/WheatField'
import {Color} from './cards/Card'

/**
 * A Game of Machi Koro
 * Events are emitted during the turns from here
 */
export default class Game extends EventEmitter {
	/**
	 * Start a game with some players
	 */
	constructor({
		quantity = require('./../config/deck.json'),
	}) {
		super()
		this.players = []
		this.playing = false
		this.deck = new Deck(quantity)
		this.emit('ready')
	}

	/**
	 * @param {Player} player
	 * @return {Game}
	 */
	addPlayer(player) {
		this.players.push(player)
		return this
	}

	/**
	 * Starts up a game
	 */
	start() {
		if(this.players.length < 2)
			throw Error('Not enough players to start')

		this.emit('game:starting')

		// give starting cards & coins to players
		for(let player of this.players) {
			player.setGame(this)
			player.coins = 3
			for (let card of [new WheatField]) {
				card.setOwner(player)
				this.deck.ownedCards.push(card)
			}
		}

		this.turn = 0
		this.players = shuffle(this.players)
		this.playing = true
	}

	/**
	 * Number of rounds
	 * @returns {number}
	 */
	get round() {
		return Math.floor(this.turn / this.players.length)
	}

	/**
	 * Roll a Dice
	 * @returns {number} 1-6
	 */
	static rollDice() {
		return Math.floor(Math.random() * 5 + 1)
	}

	/**
	 * Just keep doing turns
	 */
	async play() {
		if(!this.playing)
			this.start()
		while(await this.doTurn());
	}

	/**
	 * Roll the dice for a player
	 * @param {Player} player
	 * @return {Promise.<{dice: number[], roll: number}>}
	 */
	async doDiceRolls(player) {
		let again = false
		let dice

		do {
			dice = [this.constructor.rollDice()]
			if (await player.actions.roll2Dice.call(player))
				dice.push(this.constructor.rollDice())

			this.emit('turn:roll', {
				number: this.turn,
				roll:   dice,
				player,
			})

			// can only rollAgain once per turn
			if(again)
				again = false
			else
				again = await player.actions.rollAgain.call(player, dice)
		} while (again)

		let roll = 0
		for (let num of dice)
			roll += num

		if(await player.actions.add2ToRoll.call(player, dice))
			roll += 2

		this.emit('turn:rolled', {
			number: this.turn,
			roll,
			dice,
			player,
		})

		return {dice, roll}
	}

	/**
	 * Activate cards for a player
	 * @param {Player} player
	 * @param {number} roll
	 */
	doActivateCards(player, roll) {
		// activate OTHER REDs
		for (let other of this.players)
			for (let card of other.cards)
				if (card.color === Color.RED) {
					if (card.rolled(roll, player))
						this.emit('card:activate', {
							number: this.turn,
							card,
							roller: player,
							owner: card.owner,
						})
				}

		// activate ALL BLUEs
		for (let card of this.deck.ownedCards)
			if (card.color === Color.BLUE) {
				if (card.rolled(roll))
					this.emit('card:activate', {
						number: this.turn,
						roller: player,
						owner: card.owner,
						card,
					})
			}

		// activate YOUR GREENs
		for (let card of player.cards)
			if (card.color === Color.GREEN) {
				if (card.rolled(roll))
					this.emit('card:activate', {
						number: this.turn,
						roller: player,
						owner: card.owner,
						card,
					})
			}

		// activate YOUR PURPLEs
		for (let card of player.cards)
			if (card.color === Color.PURPLE) {
				if (card.rolled(roll))
					this.emit('card:activate', {
						number: this.turn,
						roller: player,
						owner: card.owner,
						card,
					})
			}
	}

	/**
	 * Request buying cards from deck
	 * @param {Player} player
	 * @return {Promise.<Card[]>}
	 */
	async doBuyCards(player) {
		this.deck.updateMarket()

		const index = await player.actions.buyFromMarket.call(player, this.deck.market)
		let cards = []

		if(index.length) {
			cards = this.deck.buy(index, player)

			// emit individually
			for (let card in cards)
				this.emit('buy:card', {
					number: this.turn,
					player,
					card,
				})
		}

		return cards
	}

	/**
	 * Request buying landmarks
	 * @param {Player} player
	 * @return {Promise.<string[]>}
	 */
	async doBuyLandmarks(player) {
		const landmarks = await player.actions.buyLandmarks.call(player)

		if(landmarks.length) {
			// emit individually
			for(let landmark of landmarks) {
				if(player.buyLandmark(landmark))
					this.emit('buy:landmark', {
						number: this.turn,
						player,
						landmark,
					})
			}
		}

		return landmarks
	}

	/**
	 * Simulate one turn
	 * @throws
	 */
	async doTurn() {
		if(!this.playing || !this.players.length)
			return false

		this.turn++

		let player = this.players.shift()

		this.emit('turn:start', {
			number: this.turn,
			player,
		})

		const {dice, roll} = await this.doDiceRolls(player)

		await this.doActivateCards(player, roll)
		await this.doBuyCards(player)
		await this.doBuyLandmarks(player)

		// check for winner
		if (player.won()) {
			this.emit('game:over', {player})
			this.playing = false
		}

		// move player to start or end
		if (this.playing) {
			if (await player.actions.turnAgain.call(player, dice))
				this.players.unshift(player)
			else
				this.players.push(player)
		}

		this.emit('turn:done', {
			number: this.turn,
			player,
		})

		return true
	}
}
